/**
 * Screen 05 — stocktake.
 *
 * The prototype walks one item at a time; this walks the same sequence as a
 * list, because a person counting a bar looks up and down it and a tablet
 * that only ever shows one bottle cannot be checked against the shelf. The
 * entry itself is the prototype's: two big tiles and a keypad, tenths or
 * grams, with the resulting millilitres and the variance band under them.
 *
 * The count arithmetic is `countLine` from @baros/core — the same function
 * the API runs when it stores the line, so what the screen shows before the
 * save and what comes back after it are the same number by construction.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  bottleSpec,
  countLine,
  fullBottleKg,
  type CountHeader,
  type CountLine,
  type CountMethod,
  type CountRaw,
  type Ingredient,
  type StockLevel,
  type VarianceBand,
} from "@baros/core";
import { ApiError } from "../api.js";
import { useApp } from "../context.js";
import { shortDate } from "../format.js";
import { NoVenue, useActingVenue } from "../NoVenue.js";
import type { ScreenProps } from "../screens.js";
import { stockApi, type LineReading } from "./api.js";
import "./stock.css";

/** The prototype's 3 % / 8 % thresholds, in the prototype's colours. */
const BAND_CLASS: Readonly<Record<VarianceBand, string>> = {
  ok: "pos",
  watch: "warn",
  investigate: "neg",
};

const KEYS = ["7", "8", "9", "4", "5", "6", "1", "2", "3", ".", "0", "⌫"];

const ml = (n: number) => `${n.toFixed(0)} mL`;

const signed = (n: number, digits = 0) =>
  `${n >= 0 ? "+" : ""}${n.toFixed(digits)}`;

interface Row {
  readonly ingredientId: string;
  readonly name: string;
  readonly location: string;
  readonly expectedMl: number;
  readonly ingredient: Ingredient | undefined;
  readonly line: CountLine | undefined;
}

export function StockScreen(_props: ScreenProps) {
  const app = useApp();
  const { token, library } = app;
  const venueId = useActingVenue();
  const canCount = app.me.can["stock:count"];
  const canAdjust = app.me.can["stock:adjust"];

  const [tab, setTab] = useState<"count" | "levels">("count");
  const [levels, setLevels] = useState<readonly StockLevel[]>([]);
  const [header, setHeader] = useState<CountHeader | null>(null);
  const [lines, setLines] = useState<ReadonlyMap<string, CountLine>>(new Map());
  const [mode, setMode] = useState<CountMethod>("tenths");
  const [openId, setOpenId] = useState<string | null>(null);
  const [field, setField] = useState<"bottles" | "part">("bottles");
  const [bottles, setBottles] = useState("");
  const [part, setPart] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const ingredients = useMemo(
    () => new Map(library.ingredients.map((i) => [i.ingredientId, i])),
    [library.ingredients],
  );

  /**
   * Levels, the count list, and the open count if there is one.
   *
   * A count survives a reload — somebody counts the back bar, the tablet
   * sleeps, they come back to it — so the newest header is picked up rather
   * than a fresh one being started.
   */
  const load = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    setError(null);
    try {
      const [got, list] = await Promise.all([
        stockApi.levels(token, venueId),
        stockApi.counts(token, venueId),
      ]);
      setLevels(got.stock);
      const openCount = list.counts.find((c) => c.status === "open") ?? null;
      setHeader(openCount);
      if (openCount) {
        setMode(openCount.method);
        const full = await stockApi.count(token, venueId, openCount.countId);
        setLines(new Map(full.lines.map((l) => [l.ingredientId, l])));
      } else {
        setLines(new Map());
      }
    } catch (err) {
      setError(message(err));
    } finally {
      setLoading(false);
    }
  }, [token, venueId]);

  useEffect(() => {
    void load();
  }, [load]);

  const rows: readonly Row[] = useMemo(() => {
    return [...levels]
      .map((level) => {
        const ingredient = ingredients.get(level.ingredientId);
        return {
          ingredientId: level.ingredientId,
          name: ingredient?.name ?? level.ingredientId,
          location: level.location ?? "Unfiled",
          expectedMl: level.heldMl,
          ingredient,
          line: lines.get(level.ingredientId),
        };
      })
      .sort(
        (a, b) => a.location.localeCompare(b.location) || a.name.localeCompare(b.name),
      );
  }, [levels, ingredients, lines]);

  const counted = rows.filter((r) => r.line).length;
  const pct = rows.length ? Math.round((counted / rows.length) * 100) : 0;

  const openRow = rows.find((r) => r.ingredientId === openId);

  /** The reading as it stands in the two tiles. */
  const raw: CountRaw = useMemo(
    () =>
      mode === "tenths"
        ? { bottles: Number(bottles || 0), tenths: Number(part || 0) }
        : { bottles: Number(bottles || 0), grams: Number(part || 0) },
    [mode, bottles, part],
  );

  const spec = bottleSpec(openRow?.ingredient);
  const preview =
    openRow && (bottles !== "" || part !== "")
      ? countLine({
          countId: header?.countId ?? "draft",
          ingredientId: openRow.ingredientId,
          method: mode,
          raw,
          expectedMl: openRow.expectedMl,
          spec,
        })
      : null;

  const select = (row: Row) => {
    if (openId === row.ingredientId) {
      setOpenId(null);
      return;
    }
    setOpenId(row.ingredientId);
    setField("bottles");
    // Seed the tiles from the line already recorded, so a correction starts
    // from what is stored rather than from nothing.
    const prior = row.line?.raw;
    setBottles(prior && "bottles" in prior && prior.bottles ? String(prior.bottles) : "");
    setPart(
      prior && "tenths" in prior && mode === "tenths"
        ? String(prior.tenths)
        : prior && "grams" in prior && mode === "weigh"
          ? String(prior.grams)
          : "",
    );
  };

  const tap = (key: string) => {
    const set = field === "bottles" ? setBottles : setPart;
    set((current) => {
      if (key === "⌫") return current.slice(0, -1);
      if (key === "." && current.includes(".")) return current;
      return (current + key).slice(0, 6);
    });
  };

  const startCount = async () => {
    if (!venueId) return;
    setBusy(true);
    setError(null);
    try {
      const started = await stockApi.startCount(token, venueId, mode);
      setHeader(started.count);
      setLevels(started.stock);
      setLines(new Map());
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  };

  const saveLine = async () => {
    if (!venueId || !header || !openRow) return;
    const reading: LineReading =
      mode === "tenths"
        ? { method: "tenths", bottles: Number(bottles || 0), tenths: Number(part || 0) }
        : { method: "weigh", bottles: Number(bottles || 0), grams: Number(part || 0) };
    setBusy(true);
    setError(null);
    try {
      const saved = await stockApi.saveLine(
        token,
        venueId,
        header.countId,
        openRow.ingredientId,
        reading,
      );
      setLines((current) => new Map(current).set(openRow.ingredientId, saved.line));
      setHeader({ ...header, lineCount: saved.lineCount });
      // Straight on to the next uncounted item, the way the prototype's
      // "Save and next" does — a count is a walk, not a form.
      const next = rows.find((r) => !r.line && r.ingredientId !== openRow.ingredientId);
      if (next) select(next);
      else setOpenId(null);
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  };

  const finalise = async () => {
    if (!venueId || !header) return;
    setBusy(true);
    setError(null);
    try {
      await stockApi.finalise(token, venueId, header.countId);
      setOpenId(null);
      await load();
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  };

  if (!venueId) return <NoVenue what="a stocktake" />;

  return (
    <div className="screen">
      <div className="stock-strip">
        <span className="label" style={{ fontSize: 11, letterSpacing: "0.14em" }}>
          {header ? `${counted} of ${rows.length} counted` : `${rows.length} lines on the sheet`}
        </span>
        <span className="stock-bar">
          <i style={{ width: `${header ? pct : 0}%` }} />
        </span>
        <span className="label" style={{ fontSize: 11, letterSpacing: "0.14em" }}>
          {openRow ? openRow.location : header ? "Count open" : "No count open"}
        </span>
      </div>

      <div className="stock-head">
        <div>
          <div className="label">Count sequence, location-led</div>
          <div className="h">
            {header
              ? `Counting since ${shortDate(header.startedAt)} · ${header.method === "tenths" ? "bottles and tenths" : "by weight"}`
              : "Nothing is being counted."}
          </div>
        </div>
        <span className="spacer" />
        <button className="btn" aria-pressed={tab === "count"} onClick={() => setTab("count")}>
          Count
        </button>
        <button className="btn" aria-pressed={tab === "levels"} onClick={() => setTab("levels")}>
          Stock levels
        </button>
      </div>

      {error && (
        <div style={{ padding: "0 20px 10px" }}>
          <div className="msg err">{error}</div>
        </div>
      )}

      {tab === "count" ? (
        <>
          <div className="row" style={{ padding: "0 20px 12px" }}>
            <span className="label" style={{ width: 52, flex: "0 0 52px" }}>
              Method
            </span>
            <button
              className="btn"
              style={{ height: 56 }}
              aria-pressed={mode === "tenths"}
              onClick={() => switchMode("tenths")}
            >
              Bottles and tenths
            </button>
            <button
              className="btn"
              style={{ height: 56 }}
              aria-pressed={mode === "weigh"}
              onClick={() => switchMode("weigh")}
            >
              By weight
            </button>
            <span className="spacer" />
            {!header && canCount && (
              <button className="btn primary" disabled={busy} onClick={() => void startCount()}>
                Start a count
              </button>
            )}
            {!header && !canCount && (
              <span className="label">Your role does not allow counting.</span>
            )}
          </div>

          <div className="scroll" style={{ padding: "0 20px 20px" }}>
            {loading && <div className="pad label">Reading the shelf…</div>}
            {!loading && rows.length === 0 && (
              <div className="pad label">
                No stock levels for this venue yet. A manager can set one from Stock levels.
              </div>
            )}
            <div className="table">
              {rows.map((row, i) => (
                <CountRow
                  key={row.ingredientId}
                  row={row}
                  showLocation={rows[i - 1]?.location !== row.location}
                  expanded={openId === row.ingredientId}
                  disabled={!header || !canCount}
                  onSelect={() => select(row)}
                >
                  {openId === row.ingredientId && header && (
                    <div className="count-pad">
                      <div className="left">
                        <div className="label">
                          {`Full bottle ${spec.bottleMl} mL · ${(row.expectedMl / spec.bottleMl).toFixed(1)} expected · full ${(fullBottleKg(spec) * 1000).toFixed(0)} g gross, empty ${(spec.tareKg * 1000).toFixed(0)} g at ${spec.density.toFixed(2)} g/mL`}
                        </div>
                        <div className="count-tiles">
                          <button
                            className="count-tile"
                            aria-pressed={field === "bottles"}
                            onClick={() => setField("bottles")}
                          >
                            <span className="t">Full bottles</span>
                            <span className="v">{bottles === "" ? "0" : bottles}</span>
                          </button>
                          <button
                            className="count-tile"
                            aria-pressed={field === "part"}
                            onClick={() => setField("part")}
                          >
                            <span className="t">
                              {mode === "tenths"
                                ? "Part bottle · tenths"
                                : "Open bottle · grams gross"}
                            </span>
                            <span className="v">{part === "" ? "0" : part}</span>
                          </button>
                        </div>
                        <div className="count-running">
                          <span className="a">
                            {preview ? `${ml(preview.countedMl)} counted` : "—"}
                          </span>
                          <span className={`b ${preview ? BAND_CLASS[preview.band] : "muted"}`}>
                            {preview
                              ? `${signed(preview.deltaMl)} mL against expected · ${signed(preview.percent, 1)}%`
                              : "Enter a count"}
                          </span>
                        </div>
                        <div className="row">
                          <button
                            className="btn"
                            style={{ height: 56 }}
                            onClick={() => {
                              setBottles("");
                              setPart("");
                            }}
                          >
                            Clear
                          </button>
                          <button className="btn" style={{ height: 56 }} onClick={() => setOpenId(null)}>
                            Skip item
                          </button>
                          <span className="spacer" />
                          <button
                            className="btn primary"
                            disabled={busy || !preview}
                            onClick={() => void saveLine()}
                          >
                            Save and next
                          </button>
                        </div>
                      </div>
                      <div className="keys">
                        {KEYS.map((k) => (
                          <button key={k} onClick={() => tap(k)}>
                            {k}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </CountRow>
              ))}
            </div>
          </div>

          {header && (
            <div className="foot">
              <span className="label" style={{ alignSelf: "center", flex: 1 }}>
                {counted} of {rows.length} lines counted ·{" "}
                {rows.length - counted === 0
                  ? "ready to finalise"
                  : `${rows.length - counted} still to count`}
              </span>
              {canAdjust ? (
                <button
                  className="btn primary"
                  disabled={busy || counted === 0}
                  onClick={() => void finalise()}
                >
                  Finalise count
                </button>
              ) : (
                <span className="label" style={{ alignSelf: "center" }}>
                  A manager finalises the count.
                </span>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="scroll" style={{ padding: "0 20px 20px" }}>
          <div className="table lvl-table">
            <div className="thead">
              <span className="nm">Ingredient</span>
              <span className="loc">Location</span>
              <span className="held">Held</span>
              <span className="btl">Bottles</span>
              <span className="upd">Updated</span>
            </div>
            {rows.map((row) => {
              const s = bottleSpec(row.ingredient);
              return (
                <div className="lvl-row" key={row.ingredientId}>
                  <span className="nm">{row.name}</span>
                  <span className="loc">{row.location}</span>
                  <span className="held">{ml(row.expectedMl)}</span>
                  <span className="btl">{(row.expectedMl / s.bottleMl).toFixed(1)} × {s.bottleMl}</span>
                  <span className="upd">
                    {shortDate(levels.find((l) => l.ingredientId === row.ingredientId)?.updatedAt)}
                  </span>
                </div>
              );
            })}
            {rows.length === 0 && !loading && (
              <div className="pad label">No stock levels for this venue.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  /** Switching method clears the part field — tenths and grams are not the same number. */
  function switchMode(next: CountMethod) {
    setMode(next);
    setPart("");
    setField("bottles");
  }
}

interface RowProps {
  readonly row: Row;
  readonly showLocation: boolean;
  readonly expanded: boolean;
  readonly disabled: boolean;
  readonly onSelect: () => void;
  readonly children?: ReactNode;
}

function CountRow({ row, showLocation, expanded, disabled, onSelect, children }: RowProps) {
  const line = row.line;
  return (
    <>
      {showLocation && <div className="loc-head label">{row.location}</div>}
      <button
        className="count-row"
        aria-expanded={expanded}
        disabled={disabled}
        onClick={onSelect}
      >
        <span className="c-dot">
          <span
            className="dot"
            style={{ background: line ? `var(--${BAND_CLASS[line.band]})` : "transparent" }}
          />
        </span>
        <span className="nm">{row.name}</span>
        <span className="loc">{row.location}</span>
        <span className="exp">{ml(row.expectedMl)}</span>
        <span className="got">{line ? ml(line.countedMl) : "—"}</span>
        <span className={`var ${line ? BAND_CLASS[line.band] : "muted"}`}>
          {line
            ? `${signed(line.deltaMl)} mL · ${signed(line.percent, 1)}%`
            : "Not counted"}
        </span>
        <span className="caret">{expanded ? "▾" : "▸"}</span>
      </button>
      {children}
    </>
  );
}

function message(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return err instanceof Error ? err.message : "The stocktake could not be loaded";
}
